import { useQuery, useQueryList } from '@percent/utility'
import { usePostHogFeatureFlag } from '../usePostHogFeatureFlag/usePostHogFeatureFlag'
import { POSTHOG_FEATURE_FLAGS } from '../usePostHogFeatureFlag/posthogFeatureFlags'
import { useFeatureFlagsReady } from '../usePostHogFeatureFlag/useFeatureFlagsReady'
import { useServices } from '@percent/cause-dashboard/context/serviceContext/ServiceContext'
import {
  CausesPortalOpportunityDto,
  GetVolunteeringOrganisationCreatedOpportunitiesQuery
} from '@percent/cause-dashboard/services/volunteering/volunteeringService.types'
import { useAuthState } from '../useAuthState/useAuthState'

export type UseCanUseVolunteeringResult = Readonly<{
  canUseVolunteering: boolean
  isPending: boolean
  isLoading: boolean
}>

export const useCanUseVolunteering = (): UseCanUseVolunteeringResult => {
  const volunteeringSection = usePostHogFeatureFlag(POSTHOG_FEATURE_FLAGS.CP_VOLUNTEERING_SECTION)
  const isReady = useFeatureFlagsReady()
  const { volunteeringService } = useServices()
  const { authState } = useAuthState()
  const organisationId = authState.user?.organisationId

  const {
    data: volunteeringPartners,
    status: partnersStatus,
    isLoading: isLoadingPartners
  } = useQuery(
    ['volunteering-partners'],
    volunteeringService.getVolunteeringPartners,
    {},
    { enabled: volunteeringSection }
  )

  const [{ totalResults, status: opportunitiesStatus, isLoading: isLoadingOpportunities }] = useQueryList<
    GetVolunteeringOrganisationCreatedOpportunitiesQuery,
    CausesPortalOpportunityDto
  >(
    ['has-volunteering-opportunities', organisationId],
    volunteeringService.getOrganisationCreatedOpportunities,
    {
      organisationId: organisationId as string,
      pageSize: 1
    },
    { enabled: !!organisationId }
  )

  return {
    isPending: !isReady || partnersStatus === 'pending' || opportunitiesStatus === 'pending',
    isLoading: !isReady || isLoadingPartners || isLoadingOpportunities,
    canUseVolunteering: volunteeringSection && (!!totalResults || !!volunteeringPartners?.length)
  }
}
